import { Role, rolesManipulator } from 'auth';
import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { VerificationLoadingScreen } from '@/modules/entity/components/VerificationLoadingScreen.tsx';
import { RenderAssociationEntityDetails } from '@/modules/entity/components/entity-info/AssociationEntityInfo.tsx';
import { RenderCompanyEntityDetails } from '@/modules/entity/components/entity-info/CompanyEntityInfo.tsx';
import { RenderIndividualEntityDetails } from '@/modules/entity/components/entity-info/IndividualEntityInfo.tsx';
import { RenderPartnershipEntityDetails } from '@/modules/entity/components/entity-info/PartnershipEntityInfo.tsx';
import { RenderSmsfEntityDetails } from '@/modules/entity/components/entity-info/SmsfEntityInfo.tsx';
import { RenderTrustEntityDetails } from '@/modules/entity/components/entity-info/TrustEntityInfo.tsx';
import { ShareholderDetails } from '@/modules/entity/types/EntityItemResponse.ts';
import { OnboardingHolderWrapper } from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderWrapper.tsx';
import { OnboardingStepProps } from '@/modules/issuer-dashboard/components/holder-create/roadsTypes.tsx';
import {
  HOLDERS_TAB_NAME,
  HoldersTabNames,
} from '@/modules/issuer-dashboard/pages/IssuerHolders.tsx';
import {
  useInvestorEditChangeStatusToAwaitingApprovalMutation,
  useIssuerGetInvestorEditByIdQuery,
} from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { CustomCheckbox } from 'ui';
import { AlertOrangeIcon } from 'ui/icons';
import { EntityType, InvestorEditsStatus } from 'api';

export const ConfirmationHolder = ({ allowSkip, roads }: OnboardingStepProps) => {
  const [params, setParams] = useSearchParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { id: entity_id = 0 } = useParams();
  const { data: dataHolder } = useIssuerGetInvestorEditByIdQuery(
    entity_id ? +entity_id : -1,
    !entity_id || +entity_id === 0 || isNaN(+entity_id),
  );

  const isUpdate = useMemo(
    () =>
      state?.action === 'update' ||
      !!dataHolder?.data?.old_data?.shareholder_id ||
      !!dataHolder?.data?.new_data?.entity_data?.document_files?.length,
    [dataHolder?.data, state?.action],
  );

  const [isInformationIsTrue, setIsInformationIsTrue] = useState(false);
  // const [isPolicy, setIsPolicy] = useState(false);
  // const [isBron, setIsBron] = useState(false);
  // const [isAcknowledgementsAndDeclarations, setIsAcknowledgementsAndDeclarations] = useState(false);

  const getSubStep = useCallback((step: number) => +(params.get(`sub_step${step}`) ?? 0), [params]);
  const getSubStepRoad = useCallback(
    (step: number, subStep: number) => +(params.get(`sub_step_road${step}_${subStep}`) ?? 0),
    [params],
  );
  const getPercentWithSubRoad = useCallback(
    (shareholders: ShareholderDetails[], step: number, allSubStep = 2) => {
      const filteredShareholders = shareholders?.filter(item => !item.same_as_index);
      if (!filteredShareholders || filteredShareholders?.length === 0) {
        return 100;
      }
      const countShareholders = (filteredShareholders?.length ?? 1) * allSubStep;
      const subStep = getSubStep(step);
      const subStepRoad = getSubStepRoad(step, subStep);
      const percent = ((subStep * allSubStep + (subStepRoad + 1)) / countShareholders) * 100;
      return percent;
    },
    [getSubStep, getSubStepRoad],
  );

  const hintStatus = useMemo(() => {
    if (!dataHolder?.data?.status) return;

    switch (dataHolder.data.status) {
      case InvestorEditsStatus.InProgress:
        return;
      case InvestorEditsStatus.Approved:
        return 'This holder has already been approved';
      case InvestorEditsStatus.Rejected:
        return 'This holder has already been rejected';
      case InvestorEditsStatus.Pending:
        return "This holder is awaiting moderation on the holders page in the Pending requests tab. You haven't changed anything to confirm again.";
      default:
        return;
    }
  }, [dataHolder?.data?.status]);

  const isFilledRequiredFields = useMemo((): {
    stepsNeedRequired: string[];
    stepsNeedRequiredAll: string[];
    completedAll: boolean;
    completed: boolean;
  } => {
    const steps = roads?.slice(0, -1); //Del Confirmation
    const stepsNeedRequiredAll: string[] = [];
    let completedAll = true;
    const stepsNeedRequired: string[] = [];
    let completed = true;

    steps?.forEach((_, i) => {
      const { getNavProgress, navName } = steps[i];
      const currentProgress = getNavProgress({
        user_data: dataHolder?.data?.new_data?.user_data,
        data: dataHolder?.data?.new_data?.entity_data,
        shareholder_id: dataHolder?.data?.shareholder_id,
        getSubStep,
        getPercentWithSubRoad,
        step: i,
      });

      if (!currentProgress.completed && (i > 2 || !isUpdate)) {
        completedAll = false;
        stepsNeedRequiredAll.push(navName);
      }
      if (!currentProgress.completed && i > 2 && i < 5) {
        completed = false;
        stepsNeedRequired.push(navName);
      }
    });

    return { stepsNeedRequired, stepsNeedRequiredAll, completed, completedAll };
  }, [
    dataHolder?.data?.new_data?.entity_data,
    dataHolder?.data?.new_data?.user_data,
    dataHolder?.data?.shareholder_id,
    getPercentWithSubRoad,
    getSubStep,
    roads,
    isUpdate,
  ]);

  const handleNextStep = useCallback(() => {
    setTimeout(() => {
      params.set(HOLDERS_TAB_NAME, HoldersTabNames.Overview);
      setParams(params, { state, replace: false });
      navigate(
        {
          pathname: issuerDashboardPaths.static.holders,
          search: params.toString(),
        },
        { replace: false, state },
      );
    });
  }, [navigate, params, setParams, state]);

  const { mutateAsync: setEntityAwaitingApproval, isLoading } =
    useInvestorEditChangeStatusToAwaitingApprovalMutation({
      onSuccess: handleNextStep,
    });

  const handleConfirm = useCallback(() => {
    setEntityAwaitingApproval(+entity_id);
    if (!isFilledRequiredFields.completedAll) {
      toast(
        t => (
          <span className="flex flex-col justify-center items-center gap-2 text-sm">
            <span>
              <img src={AlertOrangeIcon} className="w-8 h-8" alt="alert" />
            </span>
            <div>
              Your changes has been saved, but the following data points are incorrect or missing:
              <br />
              {isFilledRequiredFields.stepsNeedRequiredAll.map((step, i) => (
                <span key={i}>
                  - {step}
                  <br />
                </span>
              ))}
            </div>
            <button
              className="px-2 py-1 border rounded hover:border-blue-800"
              onClick={() => toast.dismiss(t.id)}
            >
              Dismiss
            </button>
          </span>
        ),
        {
          duration: 10000,
          id: 'unsaved_changes',
        },
      );
    }
  }, [
    entity_id,
    isFilledRequiredFields.completedAll,
    isFilledRequiredFields.stepsNeedRequiredAll,
    setEntityAwaitingApproval,
  ]);

  const isIssuerAdmin = rolesManipulator.current_user_role === Role.SR_ISSUER_ADMIN;

  const renderInfo = () => {
    if (dataHolder?.data?.new_data?.entity_data?.type === EntityType.Individual) {
      return (
        <RenderIndividualEntityDetails
          individual_entity_details={
            dataHolder?.data?.new_data?.entity_data?.individual_entity_details
          }
          {...dataHolder?.data?.new_data?.entity_data}
        />
      );
    }

    if (dataHolder?.data?.new_data?.entity_data?.type === EntityType.Company) {
      return (
        <RenderCompanyEntityDetails
          company_entity_details={dataHolder?.data?.new_data?.entity_data?.company_entity_details}
          {...dataHolder?.data?.new_data?.entity_data}
        />
      );
    }

    if (dataHolder?.data?.new_data?.entity_data?.type === EntityType.Trust) {
      return (
        <RenderTrustEntityDetails
          trust_entity_details={dataHolder?.data?.new_data?.entity_data?.trust_entity_details}
          {...dataHolder?.data?.new_data?.entity_data}
        />
      );
    }

    if (dataHolder?.data?.new_data?.entity_data?.type === EntityType.Smsf) {
      return (
        <RenderSmsfEntityDetails
          smsf_entity_details={dataHolder?.data?.new_data?.entity_data?.smsf_entity_details}
          {...dataHolder?.data?.new_data?.entity_data}
        />
      );
    }

    if (dataHolder?.data?.new_data?.entity_data?.type === EntityType.Partnership) {
      return (
        <RenderPartnershipEntityDetails
          partnership_entity_details={
            dataHolder?.data?.new_data?.entity_data?.partnership_entity_details
          }
          {...dataHolder?.data?.new_data?.entity_data}
        />
      );
    }

    if (dataHolder?.data?.new_data?.entity_data?.type === EntityType.Association) {
      return (
        <RenderAssociationEntityDetails
          association_entity_details_id={
            dataHolder?.data?.new_data?.entity_data?.association_entity_details_id
          }
          {...dataHolder?.data?.new_data?.entity_data}
        />
      );
    }

    return <></>;
  };

  if (isLoading) {
    return <VerificationLoadingScreen />;
  }

  return (
    <OnboardingHolderWrapper
      isConfirmPage={true}
      title={'Check everything carefully'}
      messageInsteadButtons={hintStatus}
      buttonContinueName={isIssuerAdmin ? 'Confirm and Approve change' : 'Submit for moderation'}
      onClickContinue={() => {
        if (!isFilledRequiredFields.completed) {
          return;
        }
        handleConfirm();
      }}
      buttonContinueHint={
        !isFilledRequiredFields.completed ? (
          <>
            You cannot proceed with confirmation until these steps are completed:
            <br />
            {isFilledRequiredFields.stepsNeedRequired.map((step, i) => (
              <span key={i}>
                - {step}
                <br />
              </span>
            ))}
          </>
        ) : undefined
      }
      onClickSkip={allowSkip ? handleNextStep : undefined}
      buttonSkipName="Finish later"
      isLoading={isLoading}
      disableNext={!isInformationIsTrue || isLoading || !isFilledRequiredFields.completed}
    >
      <div className="w-full flex flex-col gap-5">
        {renderInfo()}
        {!hintStatus && (
          <div className="w-full flex flex-col gap-2 pt-5">
            <CustomCheckbox
              onChange={e => setIsInformationIsTrue(e.target.checked)}
              checked={isInformationIsTrue}
              label="I/We confirm the above information is true and correct"
              className="[&>.ant-checkbox]:self-start [&>.ant-checkbox]:pt-1"
            />
          </div>
        )}
      </div>
    </OnboardingHolderWrapper>
  );
};
